import { OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { FormBuilder, FormGroup, NgForm, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSelect } from '@angular/material';
import { MediaObserver } from '@angular/flex-layout';
import { of, Subject, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, first, map, takeUntil } from 'rxjs/operators';
import { ApplicationService, } from 'services/application/application.service';
import { ConfigService } from 'services/config/config.service';
import { DialogService } from 'services/dialogs/dialogs.service';
import { DownloadService, HttpMethod, DownloadFormat } from 'services/download/download.service';
import { ErrorService } from 'services/error/error.service';
import { SplashService } from 'services/splash/splash.service';
import { UserService } from 'services/user/user.service';
import { UnsavedChangesService } from 'routes/routes.guard';
import { FileUploadService } from 'modules/fileupload/fileupload.service';
import { PriorityPipe } from 'pipes/priority.pipe';
import { TitleCasePipe } from 'pipes/titlecase.pipe';
var EditRequestComponent = /** @class */ (function () {
    function EditRequestComponent(appSvc, configSvc, dlgSvc, downloadSvc, errorSvc, fb, fileUploadSvc, http, media, priorityPipe, route, router, splashSvc, titleCasePipe, unsavedChangesSvc, userSvc) {
        this.appSvc = appSvc;
        this.configSvc = configSvc;
        this.dlgSvc = dlgSvc;
        this.downloadSvc = downloadSvc;
        this.errorSvc = errorSvc;
        this.fb = fb;
        this.fileUploadSvc = fileUploadSvc;
        this.http = http;
        this.media = media;
        this.priorityPipe = priorityPipe;
        this.route = route;
        this.router = router;
        this.splashSvc = splashSvc;
        this.titleCasePipe = titleCasePipe;
        this.unsavedChangesSvc = unsavedChangesSvc;
        this.userSvc = userSvc;
        this._subrelease = new Subject();
    }
    EditRequestComponent.prototype.ngAfterViewInit = function () {
        var _this = this;
        if (this.CategoryInput) {
            window.setTimeout(function () {
                _this.CategoryInput.focus();
            }, 0);
        }
    };
    EditRequestComponent.prototype.ngOnDestroy = function () {
        this._subrelease.next();
        this._subrelease.complete();
        // Stop tracking
        this.TakeDownTracking();
    };
    EditRequestComponent.prototype.ngOnInit = function () {
        var self = this;
        self.AllCategories = null;
        self.AllSchools = null;
        self.BoardMembers = null;
        self.ChangesDetected = false;
        // Set a flag if this is an iPad device, since those cannot do file attachment uploads
        self.IsiPad = window.navigator.userAgent.indexOf('iPad') >= 0 || window.navigator.userAgent.indexOf('iPhone') >= 0;
        self.LoadedRequestForm = null;
        self.SelectedTabIndex = 0;
        self.User = null;
        self.ViewReady = false;
        // Wait for the first non-null user info object
        self.userSvc.UserSubject.pipe(takeUntil(self._subrelease), first(function (_u) { return !!_u && !!_u.AuthToken; })).subscribe(function (_user) {
            self.User = _user;
            // Initialize the form group
            self.BuildRequestFormGroup();
            // Subscribe to media size changes.
            self.media.media$.pipe(takeUntil(self._subrelease)).subscribe(function (m) {
                self.SmallSize = m.mqAlias === 'xs' || m.mqAlias === 'sm';
            });
            // Subscribe to constant user changes, to keep AuthToken in sync.
            self.userSvc.UserSubject.pipe(takeUntil(self._subrelease)).subscribe(function (user) {
                self.User = user;
            }, function (error) { return self.errorSvc.HandleServiceError(error); });
            // Load data for component
            combineLatest(self.appSvc.GetAllSchools(), self.appSvc.GetAllCategories(), self.appSvc.GetAllBoardMembers(), self.route.params)
                .pipe(takeUntil(self._subrelease), map(function (_p) {
                return {
                    Schools: _p[0],
                    Categories: _p[1],
                    Board: _p[2],
                    Params: _p[3],
                };
            }))
                .subscribe(function (packet) {
                self.AllSchools = packet.Schools || [];
                self.AllCategories = packet.Categories || [];
                self.BoardMembers = packet.Board || [];
                self.ParamsRequestID = packet.Params ? +packet.Params['RequestID'] : null;
                self.ParamsPreviousPage = packet && packet.Params['PreviousPage'];
                // Assuming we have a request ID, load that request so we can populate the form.
                self.appSvc.GetRequest(self.User.AuthToken, self.User.CurrentRole, self.ParamsRequestID, true).subscribe(function (request) {
                    // Load/patch the form with the loaded request information
                    self.UpdateRequestForm(request);
                    // Set up form tracking by backing up the request form contents
                    self.LoadedRequestForm = request;
                    self.OriginalRequestInfo = JSON.stringify(self.RequestForm.getRawValue());
                    self.ChangesDetected = false;
                    self.SetupTracking();
                    self.ViewReady = true;
                }, function (error) { return self.errorSvc.HandleServiceError(error); });
            }, function (error) { return self.errorSvc.HandleServiceError(error); });
        }, function (error) { return self.errorSvc.HandleServiceError(error); });
    };
    // #region Assign Request
    EditRequestComponent.prototype.AssignRequest = function () {
        var self = this;
        if (!self.User) {
            return;
        }
        var formval = self.RequestForm.getRawValue(); // Get raw value because of disabled entries
        // For this, we don't have to upload attachments, just basic values for the request. Use an alternative endpoint in the API layer.
        self.splashSvc.OpenSplash({ TitleText: 'Please Wait', MessageText: 'Assigning request...' }).subscribe(function () {
            self.appSvc.AssignRequest(self.User.AuthToken, self.User.CurrentRole, formval).subscribe(function (referralID) {
                self.splashSvc.CloseSplash();
                self.splashSvc.SnackbarSuccess("Request #" + formval.RequestIssueNumber + " Assigned Successfully", 5000);
                // If we have a previous page, go back to it. Otherwise, go back to the home page for the user.
                if (self.ParamsPreviousPage) {
                    self.router.navigate([self.ParamsPreviousPage]);
                }
                else {
                    self.router.navigate(['home']);
                }
            }, function (error) { return self.errorSvc.HandleServiceError(error); });
        });
    };
    // #endregion
    // #region Build Request Form Group
    EditRequestComponent.prototype.BuildRequestFormGroup = function () {
        var self = this;
        // Build the request form
        self.RequestForm = self.fb.group({
            RequestID: [null],
            Subject: ['', Validators.required],
            RequestCategory: [null, Validators.required],
            DueDate: [null, Validators.required],
            Priority: [null, Validators.required],
            LoadedPriority: [null],
            Confidential: [false],
            Source: [0, Validators.required],
            SourceDescription: [''],
            RequestBoardMember: [null, Validators.required],
            SchoolNumber: [''],
            BoardDistrict: [''],
            RequestText: ['', Validators.required],
            RequestIssueNumber: [null, Validators.required],
            RequestAttachments: self.fb.array([]),
            RequestedBySAPID: ['', Validators.required],
            SourceNotes: [''],
            WarnedPriority: [false],
        });
        // For editing requests, certain fields should be locked and disabled, for no editing.
        self.RequestForm.get('Subject').disable();
        self.RequestForm.get('RequestText').disable();
        // #region Setup new request subscriptions
        // Watch for changes to the priority. When it changes, either set or reset the request due date based upon the set priority.
        self.RequestForm.get('Priority')
            .valueChanges.pipe(distinctUntilChanged(), debounceTime(250))
            .subscribe(function (value) {
            // If there was a loaded priority for this request (meaning it came from a board member), and this attempt
            // is trying to change the priority, AND we have not yet warned about this, pop up a warning asking
            // if they are sure they wish to proceed. Otherwise, just proceed.
            var loadedPriority = self.RequestForm.get('LoadedPriority').value;
            var warned = self.RequestForm.get('WarnedPriority').value;
            var requestor = self.LoadedRequestForm && self.LoadedRequestForm.RequestorInfo;
            (loadedPriority != null && loadedPriority !== value && !warned
                ? // Warn about the change.
                    self.dlgSvc.OpenConfirmationDialog({
                        TitleText: 'Lower Priority Alert',
                        MessageText: "You are about to change the priority for this request from the original priority " +
                            ("as specified by the originating board member for this request " + (requestor
                                ? " (" + self.titleCasePipe.transform(requestor.FirstName) + " " + self.titleCasePipe.transform(requestor.LastName) + ")"
                                : '') + " as follows:") +
                            ("<br /><br />Original Priority: <strong>" + self.priorityPipe.transform(loadedPriority) + "</strong><br />") +
                            ("New Priority: <strong>" + self.priorityPipe.transform(value) + "</strong><br /><br />") +
                            "Once this is done, the originating board member for this request will be notified of " +
                            "this change in priority via email.<br /><br />Do you wish to continue?",
                        OKText: 'Yes - Change Priority',
                        CancelText: 'No - Restore Priority',
                    })
                : // Proceed anyway
                    of(true)).subscribe(function (result) {
                if (loadedPriority !== value) {
                    self.RequestForm.get('WarnedPriority').setValue(true, { emitEvent: false });
                }
                if (!result) {
                    // The user has opted not to continue. Restore the form control back to the loaded priority without triggering an update.
                    self.RequestForm.get('Priority').setValue(loadedPriority, { emitEvent: false });
                    return;
                }
                // Continue with changing the priority.
                var addedDays = 0;
                if (value === 4) {
                    // Low priority. Wipe out the due date (if currently set)
                    self.RequestForm.get('DueDate').setValue('');
                    return;
                }
                switch (value) {
                    case 1: // Urgent - 24 hours
                        addedDays = 1;
                        break;
                    case 2: // High - 2 days
                        addedDays = 2;
                        break;
                    case 3: // Routine - 5 days
                        addedDays = 5;
                        break;
                }
                if (addedDays > 0) {
                    // Get the new due date
                    var dueDate = self.appSvc.GetDueDate(addedDays);
                    // Set the form property now
                    self.RequestForm.get('DueDate').setValue(dueDate);
                }
            });
        });
        // Watch for changes to the school number, so we can tie the district selection to the school's district or the board selection.
        // If the school is changing to a different district than the previously selected district or board member, we need to warn
        // and decide how to handle this.
        self.RequestForm.get('SchoolNumber')
            .valueChanges.pipe(distinctUntilChanged(), debounceTime(250))
            .subscribe(function (value) {
            if (!value) {
                return;
            }
            // Try to find this school via the school number set.
            var school = (self.AllSchools || []).find(function (s) { return s.SchoolNumber === value; });
            if (school && school.District && school.District !== 'X') {
                var rdistrict = self.RequestForm.get('BoardDistrict').value;
                var rboard = self.RequestForm.get('SourceDescription').value;
                var wrongDistrict_1 = !!rdistrict && rdistrict !== school.District;
                var wrongBoard_1 = !!rboard && rboard.District !== school.District;
                if (wrongDistrict_1 || wrongBoard_1) {
                    // There is a mismatch in place. Alert and get a desired action to handle this.
                    var msg = "The school which was just selected " + school.SchoolName + " - District " + school.District + " appears " +
                        "to have a different district than ";
                    var issues = '';
                    if (wrongDistrict_1 && wrongBoard_1) {
                        msg +=
                            "both the board member (" + rboard.FirstName + " " + rboard.LastName + " - District " + rboard.District + ") " +
                                ("and the board district (District " + rdistrict + ")");
                        issues = 'board member and board district';
                    }
                    else if (wrongDistrict_1) {
                        msg += "the board district (District " + rdistrict + ")";
                        issues = 'board district';
                    }
                    else {
                        msg += "the board member (" + rboard.FirstName + " " + rboard.LastName + " - District " + rboard.District + ")";
                        issues = 'board member';
                    }
                    (msg +=
                        " currently selected. Although you can proceed, this mismatch may cause issues with searching " +
                            ("for this request in the future. Would you like to remove the currently selected " + issues + ", or keep ") +
                            "the settings as they are?"),
                        self.dlgSvc
                            .OpenConfirmationDialog({
                            TitleText: 'District Mismatch(es) Detected',
                            MessageText: msg,
                            OKText: 'Remove Invalid Setting(s)',
                            CancelText: 'Keep Setting(s)',
                        })
                            .subscribe(function (result) {
                            if (result === true) {
                                // Remove the invalid board member and/or district
                                if (wrongBoard_1) {
                                    self.RequestForm.get('SourceDescription').setValue(null);
                                }
                                if (wrongDistrict_1) {
                                    self.RequestForm.get('BoardDistrict').setValue('');
                                }
                            }
                        });
                }
            }
        });
        // Watch for changes to the district, so we can remove the currently selected school if it doesn't match the new district
        self.RequestForm.get('BoardDistrict')
            .valueChanges.pipe(distinctUntilChanged(), debounceTime(250))
            .subscribe(function (value) {
            if (!value) {
                return;
            }
            // Get the currently selected board member, so we can determine the board member's district
            var rboard = self.RequestForm.get('SourceDescription').value;
            // Get the currently selected school, so we can determine the school's district
            var schoolNumber = self.RequestForm.get('SchoolNumber').value;
            var rschool = null;
            if (schoolNumber != null) {
                rschool = self.AllSchools.find(function (s) { return s.SchoolNumber === schoolNumber; });
            }
            var wrongBoard = !!rboard && rboard.District !== value;
            var wrongSchool = !!rschool && rschool.District && rschool.District !== 'X' && rschool.District !== value;
            if (wrongBoard || wrongSchool) {
                // There is a mismatch in place. Alert and get a desired action to handle this.
                var msg = "The board district which was just selected (District " + value + ") appears " +
                    "to have a different district than ";
                var issues = '';
                if (wrongSchool && wrongBoard) {
                    msg +=
                        "both the board member (" + rboard.FirstName + " " + rboard.LastName + " - District " + rboard.District + ") " +
                            ("and the school (" + rschool.SchoolName + " - District " + rschool.District + ")");
                    issues = 'board member and school';
                }
                else if (wrongSchool) {
                    msg += "the school (" + rschool.SchoolName + " - District " + rschool.District + ")";
                    issues = 'school';
                }
                else {
                    msg += "the board member (" + rboard.FirstName + " " + rboard.LastName + " - District " + rboard.District + ")";
                    issues = 'board member';
                }
                (msg +=
                    " currently selected. Although you can proceed, this mismatch may cause issues with searching " +
                        ("for this request in the future. Would you like to remove the currently selected " + issues + ", or keep ") +
                        "the settings as they are?"),
                    self.dlgSvc
                        .OpenConfirmationDialog({
                        TitleText: 'District Mismatch(es) Detected',
                        MessageText: msg,
                        OKText: 'Remove Invalid Setting(s)',
                        CancelText: 'Keep Setting(s)',
                    })
                        .subscribe(function (result) {
                        if (result === true) {
                            // Remove the invalid board member and/or school
                            if (wrongBoard) {
                                self.RequestForm.get('SourceDescription').setValue(null);
                            }
                            if (wrongSchool) {
                                self.RequestForm.get('SchoolNumber').setValue('');
                            }
                        }
                    });
            }
        });
        // Watch for changes to the board member, so we can remove the currently selected school and/or district if it doesn't match the new board's district
        self.RequestForm.get('RequestBoardMember')
            .valueChanges.pipe(distinctUntilChanged(), debounceTime(250))
            .subscribe(function (value) {
            if (!value) {
                return;
            }
            // Get the currently selected district
            var rdistrict = self.RequestForm.get('BoardDistrict').value;
            // Get the currently selected school, so we can determine the school's district
            var schoolNumber = self.RequestForm.get('SchoolNumber').value;
            var rschool = null;
            if (schoolNumber != null) {
                rschool = self.AllSchools.find(function (s) { return s.SchoolNumber === schoolNumber; });
            }
            var wrongDistrict = !!rdistrict && rdistrict !== value.District;
            var wrongSchool = !!rschool && rschool.District && rschool.District !== 'X' && rschool.District !== value.District;
            if (wrongDistrict || wrongSchool) {
                // There is a mismatch in place. Alert and get a desired action to handle this.
                var msg = "The board member which was just selected (" + value.FirstName + " " + value.LastName + " - District " + value.District + ") appears " + "to have a different district than ";
                var issues = '';
                if (wrongSchool && wrongDistrict) {
                    msg +=
                        "both the board district (District " + rdistrict + ") " +
                            ("and the school (" + rschool.SchoolName + " - District " + rschool.District + ")");
                    issues = 'board district and school';
                }
                else if (wrongSchool) {
                    msg += "the school (" + rschool.SchoolName + " - District " + rschool.District + ")";
                    issues = 'school';
                }
                else {
                    msg += "the board district (District " + rdistrict + ")";
                    issues = 'board district';
                }
                (msg +=
                    " currently selected. Although you can proceed, this mismatch may cause issues with searching " +
                        ("for this request in the future. Would you like to remove the currently selected " + issues + ", or keep ") +
                        "the settings as they are?"),
                    self.dlgSvc
                        .OpenConfirmationDialog({
                        TitleText: 'District Mismatch(es) Detected',
                        MessageText: msg,
                        OKText: 'Remove Invalid Setting(s)',
                        CancelText: 'Keep Setting(s)',
                    })
                        .subscribe(function (result) {
                        if (result === true) {
                            // Remove the invalid board district and/or school
                            if (wrongDistrict) {
                                self.RequestForm.get('BoardDistrict').setValue('');
                            }
                            if (wrongSchool) {
                                self.RequestForm.get('SchoolNumber').setValue('');
                            }
                        }
                    });
            }
        });
        self.RequestForm.valueChanges
            .pipe(takeUntil(self._subrelease), debounceTime(250), distinctUntilChanged(function (x, y) { return JSON.stringify(x) === JSON.stringify(y); }))
            .subscribe(function (value) {
            self.ChangesDetected = JSON.stringify(value) !== self.OriginalRequestInfo;
        });
        // #endregion
    };
    // #endregion
    // #region Cancel Not Info Request
    EditRequestComponent.prototype.CancelNotInfoRequest = function () {
        var self = this;
        if (!self.User) {
            return;
        }
        var formval = self.RequestForm.getRawValue();
        // Get a confirmation before proceeding
        self.dlgSvc
            .OpenConfirmationDialog({
            TitleText: 'Close Request Confirmation Requested',
            MessageText: "You are about to close this request as a request which is not for informational purposes. This action will close this request and any existing referrals, and the originating board member will be notified via email of this action. Are you sure you wish to continue?",
            OKText: 'Yes - Close Request',
            CancelText: 'No - Cancel',
        })
            .subscribe(function (result) {
            if (result === true) {
                // Confirmation provided. Close the request.
                self.splashSvc.OpenSplash({ TitleText: 'Please Wait', MessageText: 'Closing request...' }).subscribe(function () {
                    self.appSvc.CloseAsNonInfoRequest(self.User.AuthToken, self.User.CurrentRole, formval.RequestID).subscribe(function () {
                        self.splashSvc.CloseSplash();
                        self.splashSvc.SnackbarSuccess("Request #" + formval.RequestIssueNumber + " Closed As Non-Info Request Successfully", 5000);
                        // If we have a previous page, go back to it.  Otherwise, go back to the home page for the user.
                        if (self.ParamsPreviousPage) {
                            self.router.navigate([self.ParamsPreviousPage]);
                        }
                        else {
                            self.router.navigate(['home']);
                        }
                    }, function (error) { return self.errorSvc.HandleServiceError(error); });
                });
            }
        });
    };
    // #endregion
    // #region Can Deactivate
    EditRequestComponent.prototype.CanDeactivate = function (liveCheck) {
        var self = this;
        if (!self.RequestForm) {
            return true;
        }
        // Are there unsaved changes?
        if (!self.ChangesDetected) {
            // No. The user can deactivate.
            return true;
        }
        // If this is a live check (native from browser), forego the confirmation dialog as the native
        // browser will catch instead.
        if (liveCheck === true) {
            return false;
        }
        // Need to prompt
        return self.dlgSvc.OpenConfirmationDialog({
            TitleText: 'Unsaved Changes Detected',
            MessageText: "The request form appears to have unsaved changes which have not yet been submitted " +
                "to the system. If you leave this page now, your unsaved changes will be lost. Do you wish " +
                "to discard your changes and leave the page?",
            OKText: 'Yes - Discard Changes',
            CancelText: 'No - Stay On Page',
        });
    };
    // #endregion
    // #region Handle File Attachment
    EditRequestComponent.prototype.HandleFileAttachment = function (file) {
        if (!file) {
            return;
        }
        var self = this;
        // Download the selected file
        self.splashSvc.OpenSplash({ TitleText: 'Please Wait', MessageText: 'Downloading file...' }).subscribe(function () {
            self.downloadSvc
                .RetrieveDownload(HttpMethod.GET, "Api/Services/IRIS/DownloadFile", {
                AuthToken: self.User.AuthToken || '',
                FileName: file.FileName,
                FilePath: file.SharepointURL,
            }, DownloadFormat.Any)
                .subscribe(function () {
                self.splashSvc.CloseSplash();
            }, function (error) { return self.errorSvc.HandleServiceError(error); });
        });
    };
    // #endregion
    // #region Is Valid Workday
    EditRequestComponent.prototype.IsValidWorkday = function (d) {
        var isValid = this.appSvc.IsValidWorkday(d);
        return isValid;
    };
    // #endregion
    // #region Reset Request Form
    EditRequestComponent.prototype.ResetRequestForm = function (confirmationNeeded) {
        var self = this;
        (confirmationNeeded !== true
            ? of(true)
            : self.dlgSvc.OpenConfirmationDialog({
                TitleText: 'Reset Form?',
                MessageText: 'Are you sure you wish to reset the request form?',
                OKText: 'Yes - Clear',
                CancelText: 'No - Cancel',
            })).subscribe(function (result) {
            if (result === true) {
                // Patch the form with the loaded request from the beginning.
                if (self.requestfrm) {
                    self.requestfrm.resetForm();
                }
                self.UpdateRequestForm(self.LoadedRequestForm);
                // Update tracking
                self.OriginalRequestInfo = JSON.stringify(self.RequestForm.getRawValue());
                // Focus the category input
                if (self.CategoryInput) {
                    self.CategoryInput.focus();
                }
            }
        });
    };
    // #endregion
    // #region Setup Tracking
    EditRequestComponent.prototype.SetupTracking = function () {
        var self = this;
        self.unsavedChangesSvc.SetTracking(self.CanDeactivate.bind(self));
    };
    // #endregion
    // #region Take Down Tracking
    EditRequestComponent.prototype.TakeDownTracking = function () {
        this.unsavedChangesSvc.ClearTracking();
    };
    // #endregion
    // #region Update Request Form
    EditRequestComponent.prototype.UpdateRequestForm = function (req) {
        var self = this;
        if (!req) {
            return;
        }
        self.RequestForm.patchValue({
            RequestID: req.RequestID,
            Subject: req.Subject,
            RequestCategory: self.AllCategories.find(function (c) { return c.CategoryID === req.CategoryID; }),
            DueDate: req.DueDate,
            Priority: req.Priority,
            LoadedPriority: req.BoardPriority,
            Confidential: req.Confidential != null ? req.Confidential : false,
            Source: req.Source,
            SourceDescription: req.SourceDescription,
            RequestBoardMember: self.BoardMembers.find(function (b) { return b.SAPID === (req.RequestorInfo && req.RequestorInfo.SAPID); }),
            SchoolNumber: req.SchoolNumber,
            BoardDistrict: req.BoardDistrict,
            RequestText: req.RequestText,
            RequestIssueNumber: req.RequestIssueNumber,
            RequestedBySAPID: req.RequestorInfo.SAPID,
            SourceNotes: req.SourceNotes,
        });
        // If there are request attachments, set them now.
        if (req.RequestAttachments && !!req.RequestAttachments.length) {
            var filearr_1 = [];
            req.RequestAttachments.forEach(function (att) {
                filearr_1.push(self.fb.group({
                    FileName: att.FileName,
                    FileSize: att.FileSize,
                }));
            });
            self.RequestForm.setControl('RequestAttachments', self.fb.array(filearr_1));
        }
    };
    return EditRequestComponent;
}());
export { EditRequestComponent };
